import { Guid } from 'guid-typescript';

export interface IUserDashboard {
  selectedModules: number[];
  userCourses: IUserCourse[];
}

export class UserDashboard implements IUserDashboard {
  selectedModules: number[] = new Array<number>();
  userCourses: IUserCourse[] = new Array<IUserCourse>();
}

export interface IDashboardSummary {
  tagLine: string;
  strengths: string;
}

export class DashboardSummary implements IDashboardSummary {
  tagLine: string = '';
  strengths: string = '';
}

export interface IUserProfileKey {
  publicProfileKey: string;
}

export class UserProfileKey implements IUserProfileKey {
  publicProfileKey: string = '';
}

export interface IUserProfile {
  html: string;
  firstName: string;
  lastInitial: string;
  mbtiType: string;
}

export class UserProfile implements IUserProfile {
  html: string = '';
  firstName: string = '';
  lastInitial: string = '';
  mbtiType: string = '';
}

export interface IUserCourse {
  courseId: Guid;
  courseName: string;
  isCompleted: boolean;
  allowCompare: boolean;
}

export class UserCourse implements IUserCourse {
  courseId: Guid = Guid.createEmpty();
  courseName: string = '';
  isCompleted: boolean = false;
  allowCompare: boolean = true;
}

export interface IUserTopicStatus {
  topicId: string;
  lastPageVisited: number;
}

export class UserTopicStatus implements IUserTopicStatus {
  topicId: string = '';
  lastPageVisited: number = 0;
}

export interface IUserTopics {
  courseId: Guid;
  userTopicsStatus: IUserTopicStatus[];
}

export class UserTopics implements IUserTopics {
  courseId: Guid = Guid.createEmpty();
  userTopicsStatus: IUserTopicStatus[] = new Array<IUserTopicStatus>();
}

export interface ICategory {
  categoryId: number;
  categoryName: string;
  isUserSelected: boolean;
  courses: ICourse[];
}

export class Category implements ICategory {
  categoryId: number = 0;
  categoryName: string = '';
  isUserSelected: boolean = false;
  courses: ICourse[] = new Array<ICourse>();
}

export interface ICourse {
  courseId: Guid;
  courseLandingImageUrl: string;
  courseLandingImageWidth: number;
  courseLandingImageHeight: number;
  courseName: string;
  description: string;
  heading: string;
  imageUrl: string;
  imageWidth: number;
  imageHeight: number;
  numTopics: number;
  numCompletedTopics: number;
  html: string;
  isCompleted: boolean;
  isStarted: boolean;
  allowCompare: boolean;
  topics: ITopic[];
}

export class Course implements ICourse {
  courseId: Guid = Guid.createEmpty();
  courseLandingImageUrl: string = '';
  courseLandingImageWidth: number = 0;
  courseLandingImageHeight: number = 0;
  courseName: string = '';
  description: string = '';
  heading: string = '';
  imageUrl: string = '';
  imageWidth: number = 0;
  imageHeight: number = 0;
  numTopics: number = 0;
  numCompletedTopics: number = 0;
  html: string = '';
  isCompleted: boolean = false;
  isStarted: boolean = false;
  allowCompare: boolean = true;
  topics: ITopic[] = new Array<ITopic>();
}

export interface ITopic {
  topicId: string;
  topicNumber: number;
  topicName: string;
  topicCompleted: boolean;
  numPages: number;
  lastPageVisited: number;
  pages: IPage[];
}

export class Topic implements ITopic {
  topicId: string = '';
  topicNumber: number = 0;
  topicName: string = '';
  topicCompleted: boolean = false;
  numPages: number = 0;
  lastPageVisited: number = 0;
  pages: IPage[] = new Array<IPage>();
}

export interface IPage {
  pageId: string;
  pageNumber: number;
  pageTitle: string;
  html: string;
  percentageCompleted: number;
  show: boolean;
}

export class Page implements IPage {
  pageId: string = '';
  pageNumber: number = 0;
  pageTitle: string = '';
  html: string = '';
  percentageCompleted: number = 0;
  show: boolean = false;
}

export interface IUserDetails {
  firstName: string;
  lastName: string;
  personId: Guid;
  emailAddress: string;
  mbtiType: string;
  assessmentSiteBaseUrl: string;
  loginSiteBaseUrl: string;
  marketingSiteBaseUrl: string;
  siteBaseUrl: string;
  webApiSiteBaseUrl: string;
  publicProfileUrl: string;
  hasStarted: boolean;
  isElevateUser: boolean;
}

export class UserDetails implements IUserDetails {
  firstName: string = '';
  lastName: string = '';
  personId: Guid = Guid.createEmpty();
  emailAddress: string = '';
  mbtiType: string = '';
  assessmentSiteBaseUrl: string = '';
  loginSiteBaseUrl: string = '';
  marketingSiteBaseUrl: string = '';
  siteBaseUrl: string = '';
  webApiSiteBaseUrl: string = '';
  publicProfileUrl: string = '';
  hasStarted: boolean = false;
  isElevateUser: boolean = true;
}

export interface ITeamMember {
  teamMemberId: Guid;
  teamId: Guid;
  memberId: number;
  personId: Guid;
  emailAddress: string;
  firstName: string;
  lastName: string;
  initials: string;
  mbtiType: string;
  giftCode: string;
  isImported: boolean;
  isTeamLeader: boolean;
  color: string;
  currentUser: boolean;
  status: string;
  inviteDate: Date | null;
  consentToShareType: boolean;
  isRegistered: boolean;
  isElevateTeam: boolean;
}

export class TeamMember implements ITeamMember {
  teamMemberId: Guid = Guid.createEmpty();
  memberId: number = 0;
  teamId: Guid = Guid.createEmpty();
  personId: Guid = Guid.createEmpty();
  emailAddress: string = '';
  firstName: string = '';
  lastName: string = '';
  initials: string = '';
  mbtiType: string = '';
  giftCode: string = '';
  isImported: boolean = false;
  isTeamLeader: boolean = false;
  color: string = '';
  currentUser: boolean = false;
  get status(): string {
    if (!this.isRegistered) {
      return 'Not Started';
    }

    if (this.isElevateTeam) {
      if (!this.consentToShareType || this.mbtiType == null) {
        return 'In Progress';
      }
    } else {
      if (this.mbtiType == null) {
        return 'In Progress';
      }
    }
    return 'Completed';
  }
  inviteDate: Date | null = null;
  isRegistered: boolean;
  consentToShareType: boolean = false;
  isElevateTeam: boolean = false;
}

export interface ITeam {
  teamId: Guid;
  teamName: string;
  teamMbtiType: string;
  teamMembers: ITeamMember[];
  teamCommunicationStyles: string[];
  isElevateTeam: boolean;
  redirectUrl: string;
  linkBackUrl: string;
}

export class Team implements ITeam {
  teamId: Guid = Guid.createEmpty();
  teamName: string = '';
  teamMbtiType: string = '';
  teamMembers: ITeamMember[] = new Array<ITeamMember>();
  teamCommunicationStyles: string[] = new Array<string>();
  isElevateTeam: boolean = false;
  redirectUrl: string = '';
  linkBackUrl: string = '';
}

export interface ITeamDichotomiesContent {
  name: string;
  source: string;
  question: string;
  answers: string[];
  dichotomyDetail: IDichotomyDetail[];
}

export class TeamDichotomiesContent implements ITeamDichotomiesContent {
  name: string = '';
  source: string = '';
  question: string = '';
  answers: string[] = [];
  dichotomyDetail: DichotomyDetail[] = new Array<DichotomyDetail>();
}

export interface IDichotomyDetail {
  pattern: string;
  title: string;
  description: string;
}

export class DichotomyDetail implements IDichotomyDetail {
  pattern: string = '';
  title: string = '';
  description: string = '';
}

export interface IContentObject {
  key: string;
  value: string;
}

export class ContentObject implements IContentObject {
  key: string = '';
  value: string = '';
}

export interface IContentObjectWithType {
  key: string;
  type: string;
  content: string;
}

export class ContentObjectWithType implements IContentObjectWithType {
  key: string = '';
  type: string = '';
  content: string = '';
}
